import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { down, calendar } from 'static/icons/resizable';
import { useOnClickOutside } from 'hooks/useOnClickOutside/useOnClickOutside';
import mockAvatar from 'static/images/mockAvatar.png';
import { useCurrentUser, useAPlayerStatus } from 'hooks';
import { ROUTES } from 'config';
import { Avatar, Text, Icon } from 'view/atoms';
import { APlayerButton } from 'view/molecules';
import { ProfileMenu } from 'view/organisms';
import * as S from './ProfileDropdown.styles';

export const ProfileDropdown = () => {
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const handleCloseModal = () => setShowProfileModal(false);
  const onClickHandler = () => {
    handleCloseModal();
    navigate(ROUTES.aPlayer);
  };

  const profileButton = useRef<HTMLDivElement>(null);
  const profileModal = useRef<HTMLDivElement>(null);
  const logoutModal = useRef<HTMLDivElement>(null);
  const inviteModal = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    [profileModal, profileButton, logoutModal, inviteModal],
    handleCloseModal,
  );

  const user = useCurrentUser();
  const isAPlayer = useAPlayerStatus();

  const formatDate = (iso: string | undefined | null): string => {
    if (!iso) return '—';
    const [year, month, day] = iso.split('-');
    return `${day}.${month}.${year}`;
  };

  const startDate = formatDate(user?.topPerformerStartDate);
  const endDate = formatDate(user?.topPerformerEndDate);

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.login;

  return (
    <>
      <S.ProfileBox
        data-testid='Header-ProfileDropdown'
        showModal={showProfileModal}
        ref={profileButton}
        onClick={() => setShowProfileModal(!showProfileModal)}
      >
        <Avatar photo={mockAvatar} />

        {showProfileModal ? (
          <S.UpIcon src={down} alt='Collapse profile icon' />
        ) : (
          <Icon src={down} alt='Expand profile icon' />
        )}
      </S.ProfileBox>

      {showProfileModal && (
        <S.ModalProfile ref={profileModal}>
          {isAPlayer && <APlayerButton onClickHandler={onClickHandler} />}
          <S.ContentName data-testid='profile-header-section'>
            <Text kind='h4' data-testid='profile-user-name'>{userName ?? 'username'}</Text>
            <Text kind='small1' color='gray2' data-testid='profile-user-login'>
              {user?.login ?? 'username'}
            </Text>
          </S.ContentName>

          {isAPlayer && (
            <S.APlayerPeriod>
              <Icon src={calendar} alt='A-Player icon' />
              <Text kind='body'>{`${startDate} - ${endDate}`}</Text>
            </S.APlayerPeriod>
          )}

          <ProfileMenu
            logoutModalRef={logoutModal}
            inviteModalRef={inviteModal}
          />
        </S.ModalProfile>
      )}
    </>
  );
};
